// "Conversions by lead quality": how much of your conversion volume sits in your
// best scored leads. This is a cumulative gains chart, which the page used to
// label "AUC curve" and draw in the palette's worst-fit red, so the good news
// read as an error and the title read as data science.
//
// It also used to sit next to a "Tree performance" card that said the same thing
// in words a marketer could actually use ("31% of customers make 62% of
// conversions"). Those sentences are points on this curve, so they are plotted on
// it and the two cards are now one.
//
// The x axis counts scored leads, not contacted ones. Nothing here knows who was
// contacted, and labelling it that way invited a reader to compare the curve
// against their send list.
import React from 'react';
import { Card } from 'react-bootstrap';

import {
  Area,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import TreeNodePrecision from '../../../models/tree/TreeNodePrecision';
import TreePerformance from '../../../models/tree/TreePerformance';
import { EnsembleThreshold } from '../../../models/ensembling';
import { segmentBands, segmentColor, SEGMENT_LABEL } from './nodeSegment';
import './aucCurve.css';

// The Very Good blue, so the whole page draws from one palette.
//
// This was ink for a while, and for a reason worth keeping written down: an
// earlier version drew the curve in this same blue over four full-height
// segment washes, and the result read as "everything is very good". What fixed
// that was moving the segments off the plot and onto a solid ribbon on the
// axis, not the curve's colour, so the curve can come back to the palette now
// that the ribbon is the only thing carrying segment meaning. If it starts
// reading as a Very Good sweep again, the ribbon is the part to look at.
const CURVE = '#1e77cc';
const NO_MODEL = '#c3ccd6';

// The plot's own margins, restated here because the segment ribbon is HTML sat
// under the chart and has to line up with the axis to the pixel. Recharts gives
// no way to read the plot rect from outside, so the two are kept in step by
// using the same constants for both.
const Y_AXIS_WIDTH = 44;
const RIGHT_MARGIN = 12;

// Rounded, because nobody acts on the second decimal of a lead percentage.
const pct = (value: number) => `${Math.round(value)}%`;

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  if (point.population === 0) return null;

  return (
    <div className="auc-tip">
      <p className="auc-tip-lead">
        Your best {pct(point.percentTotalPopulation)} of leads hold{' '}
        <strong>{pct(point.percentTotalConversion)}</strong> of conversions
      </p>
      <p className="auc-tip-sub">
        {point.conversionRate}% convert here &middot; node
        {point.ids.length > 1 ? 's' : ''} {point.ids.join(', ')}
      </p>
    </div>
  );
};

type AUCCurveProps = {
  precisions: TreeNodePrecision[];
  /** Node ids hovered elsewhere on the page, highlighted on the curve. */
  highlightNodes?: number[] | null;
  performances?: TreePerformance[];
  thresholds?: EnsembleThreshold;
  ref?: React.RefObject<any>;
};

const AUCCurve = React.forwardRef(function AUCCurve(
  { precisions, performances, thresholds, highlightNodes }: AUCCurveProps,
  ref: React.RefObject<any>
) {
  const data = precisions.map((precision) => ({
    ...precision,
    percentTotalPopulation: Number(precision.percentTotalPopulation.toFixed(2)),
    percentTotalConversion: Number(precision.percentTotalConversion.toFixed(2)),
    conversionRate: Number((precision.conversionRate * 100).toFixed(2)),
  }));

  data.unshift(new TreeNodePrecision());

  if (!data || !data.length) {
    return null;
  }

  // The headline is the tightest reach worth acting on: the point that captures
  // the most conversions per lead, which is the whole argument for having a
  // model at all.
  const marked = (performances ?? []).filter(
    (perf) => perf.customersShare > 0 && perf.customersShare < 100
  );
  const headline = marked.length
    ? marked.reduce((best, perf) =>
        perf.conversionsShare / perf.customersShare >
        best.conversionsShare / best.customersShare
          ? perf
          : best
      )
    : null;

  // The point a hover elsewhere refers to. Previously the nodes list reached into
  // this chart's ref and drove recharts' private tooltip state, which depended on
  // its internal `formattedGraphicalItems` and on a row index lining up with a
  // data index. A prop the chart reads is the same feature without the coupling,
  // and it works for any surface that wants to point at a node.
  const highlighted = highlightNodes?.length
    ? data.find((point) =>
        point.ids?.some((id: number) => highlightNodes.includes(id))
      )
    : undefined;

  // Built from `precisions`, not `data`: `data` has already scaled the rate to a
  // percentage, and nodeSegment scales it again, which put almost every node in
  // "very good". `precisions` also has no synthetic zero row, so there is no
  // zero-width band sitting at the origin.
  const bands = segmentBands(precisions, thresholds);

  return (
    <Card className="auc-card">
      <Card.Body>
        <Card.Title className="auc-title">
          Conversions by lead quality
        </Card.Title>
        {headline && (
          <p className="auc-headline">
            Your top <strong>{pct(headline.customersShare)} of leads</strong>{' '}
            hold{' '}
            <strong>{pct(headline.conversionsShare)} of all conversions</strong>
            .
          </p>
        )}
        {/* The y axis meaning, set flat above the plot. As a rotated recharts
            label it ran along the tick numbers and collided with them at any
            column width narrow enough to matter. The dashed reference is keyed
            here rather than beside the title, which pushed the title into two
            lines in a column this narrow. */}
        <div className="auc-top">
          <span className="auc-cap">% of conversions</span>
          <span className="auc-legend-ref">
            <span className="auc-legend-dash" />
            no model
          </span>
        </div>
        <div className="auc-chart">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              data={data}
              ref={ref}
              margin={{ top: 10, right: RIGHT_MARGIN, bottom: 0, left: 0 }}
            >
              <defs>
                {/* Under the curve rather than behind the whole plot. The fit
                    segments used to be four full-height washes at 16% opacity,
                    which is where the chart got its faded, spreadsheet look;
                    they are a solid ribbon on the axis now and this fill is
                    what gives the curve its weight. */}
                <linearGradient id="aucFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CURVE} stopOpacity={0.16} />
                  <stop offset="100%" stopColor={CURVE} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              {/* Horizontal only: the vertical rules added noise without
                  helping anyone read a lead percentage. */}
              <CartesianGrid stroke="#eef1f4" vertical={false} />
              <XAxis
                type="number"
                dataKey="percentTotalPopulation"
                domain={[0, 100]}
                tickFormatter={pct}
                tick={{ fill: '#7a8798', fontSize: 12 }}
                stroke="#dfe4ea"
              />
              <YAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={pct}
                tick={{ fill: '#7a8798', fontSize: 12 }}
                stroke="#dfe4ea"
                width={Y_AXIS_WIDTH}
              />
              <Tooltip content={<CustomTooltip />} />
              {/* The diagonal is what working the list in no particular order
                  would get you. Faint and dashed so it reads as a reference,
                  not a rival series, which is how the old solid blue line
                  read. */}
              <ReferenceLine
                stroke={NO_MODEL}
                strokeDasharray="5 5"
                segment={[
                  { x: 0, y: 0 },
                  { x: 100, y: 100 },
                ]}
              />
              <Area
                dataKey="percentTotalConversion"
                stroke="none"
                fill="url(#aucFill)"
                // The fill and the curve are the same series drawn twice, so
                // only one of them gets the hover dot.
                activeDot={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="percentTotalConversion"
                stroke={CURVE}
                strokeWidth={3}
                strokeLinecap="round"
                // One dot per node. Without them the curve is a smooth sweep
                // that hides where the splits actually land, which is the one
                // thing a tree owner is looking for on it.
                dot={{ r: 2.5, fill: '#fff', stroke: CURVE, strokeWidth: 1.5 }}
                activeDot={{
                  r: 6,
                  fill: CURVE,
                  stroke: '#fff',
                  strokeWidth: 2,
                }}
                isAnimationActive={false}
              />
              {highlighted && (
                <ReferenceDot
                  x={highlighted.percentTotalPopulation}
                  y={highlighted.percentTotalConversion}
                  r={7}
                  fill={CURVE}
                  stroke="#fff"
                  strokeWidth={3}
                  isFront
                  label={{
                    value: `${pct(
                      highlighted.percentTotalConversion
                    )} of conversions`,
                    position: 'top',
                    fill: '#17212e',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
              )}
              {/* The sentences the neighbouring card used to carry, put where
                  they belong: on the points they describe. */}
              {marked.map((perf) => (
                <ReferenceDot
                  key={perf.customersShare}
                  x={perf.customersShare}
                  y={perf.conversionsShare}
                  r={4.5}
                  // Solid, against the hollow node dots. Both were hollow rings
                  // once the node dots came back, so the milestones the
                  // headline is drawn from were indistinguishable from any
                  // other node on the curve.
                  fill={CURVE}
                  stroke="#fff"
                  strokeWidth={2}
                  isFront
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div
          className="auc-scale"
          style={{ marginLeft: Y_AXIS_WIDTH, marginRight: RIGHT_MARGIN }}
        >
          {bands.length > 0 && (
            <div className="auc-ribbon">
              {bands.map((band) => (
                <span
                  key={`${band.segment}-${band.from}`}
                  className="auc-band"
                  style={{
                    left: `${band.from}%`,
                    width: `${band.to - band.from}%`,
                    background: segmentColor(band.segment),
                  }}
                  title={`${SEGMENT_LABEL[band.segment]}: ${pct(
                    band.from
                  )} to ${pct(band.to)} of leads`}
                />
              ))}
            </div>
          )}
          <div className="auc-cap auc-cap--x">
            % of leads, best scored first
          </div>
        </div>
      </Card.Body>
    </Card>
  );
});

export default AUCCurve;
